/**
 * `schemas/search.ts` — `spec.md` §6.5.
 *
 * The response carries `normalized` and `backend` on purpose: search is the part of
 * this system most likely to look broken in production, and those two fields turn
 * "search returns nothing" into a one-request diagnosis (`normalized` shows what was
 * actually searched for; `backend` shows whether FTS5 was even available).
 */
import * as z from 'zod';
import { CursorSchema, NonEmptyText, PageSchema, RowId, TotalCount, limitParam } from './common.js';
import { TagKind, TrackSummarySchema, Year } from './track.js';
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/search — request
 * ──────────────────────────────────────────────────────────────────────────── */
export const SEARCH_GROUPS = ['tracks', 'artists', 'tags', 'years'];
export const SearchGroupName = z.enum(SEARCH_GROUPS);
/**
 * `?groups=tracks,artists` — a comma list, defaulting to all four.
 *
 * `.prefault()` rather than `.default()`: in zod 4 a `.default()` value is the schema's
 * **output** type and is returned untouched, so `.default('tracks,artists,tags,years')`
 * on this pipe would hand the route the raw string instead of the parsed array.
 * `.prefault()` feeds the fallback through the pipe, and — because it sits on the input
 * side — also produces the correct `{ type: 'string', default: '…' }` JSON Schema for
 * Fastify's `useDefaults`.
 */
export const SearchGroupsParam = z
    .string()
    .max(64)
    .transform((raw) => raw
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part.length > 0))
    .pipe(z.array(SearchGroupName).min(1).max(SEARCH_GROUPS.length))
    .prefault(SEARCH_GROUPS.join(','));
/**
 * `q` is `1..128` chars and is NOT trimmed or rejected when blank: §6.5 step 1 requires
 * that a query normalising to zero tokens returns `200` with empty groups, because "a
 * user typing spaces is not an error". A `.min(1).trim()` here would turn that into a
 * 400 and lose the behaviour.
 */
export const SearchQuerySchema = z.object({
    q: z.string().min(1).max(128),
    limit: limitParam(1, 50, 30),
    cursor: CursorSchema.optional(),
    groups: SearchGroupsParam,
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/search — response
 * ──────────────────────────────────────────────────────────────────────────── */
/** Which query path actually answered. `like` means the FTS5 probe failed at boot. */
export const SEARCH_BACKENDS = ['fts5', 'trigram', 'like'];
export const SearchBackend = z.enum(SEARCH_BACKENDS);
/**
 * The columns a hit may be attributed to, in the bm25 weight order of §6.5 step 3
 * (title 8.0, performer 6.0, album 2.0, file_name 1.5, caption 1.0, search_key 3.0).
 *
 * Intentionally a separate declaration from `FTS_COLUMNS` in `text/fts.ts` (T05): that
 * one is the SQL-side allowlist and must never be derived from a wire value, so the two
 * are independent constants that happen to hold the same six names.
 */
export const SEARCH_MATCH_FIELDS = [
    'title',
    'performer',
    'album',
    'file_name',
    'caption',
    'search_key',
];
export const SearchMatchField = z.enum(SEARCH_MATCH_FIELDS);
/** A track hit: the usual summary plus which columns matched, for the underline markup. */
export const SearchTrackHitSchema = TrackSummarySchema.extend({
    matchFields: z.array(SearchMatchField),
});
export const SearchTrackPageSchema = PageSchema(SearchTrackHitSchema);
export const SearchArtistHitSchema = z.strictObject({
    id: RowId,
    name: NonEmptyText,
    trackCount: z.int().nonnegative(),
});
export const SearchArtistGroupSchema = z.strictObject({
    items: z.array(SearchArtistHitSchema),
    total: TotalCount,
});
export const SearchTagHitSchema = z.strictObject({
    id: RowId,
    kind: TagKind,
    name: NonEmptyText,
    trackCount: z.int().nonnegative(),
});
export const SearchTagGroupSchema = z.strictObject({
    items: z.array(SearchTagHitSchema),
    total: TotalCount,
});
export const SearchYearHitSchema = z.strictObject({
    year: Year,
    trackCount: z.int().nonnegative(),
});
export const SearchYearGroupSchema = z.strictObject({
    items: z.array(SearchYearHitSchema),
    total: TotalCount,
});
/**
 * Each group is optional because `?groups=` selects which are computed. An unselected
 * group is ABSENT, not present-and-empty: absent means "not asked for", empty means
 * "asked for, nothing matched", and the zero-token case of §6.5 step 1 is the second.
 */
export const SearchGroupsSchema = z.strictObject({
    tracks: SearchTrackPageSchema.optional(),
    artists: SearchArtistGroupSchema.optional(),
    tags: SearchTagGroupSchema.optional(),
    years: SearchYearGroupSchema.optional(),
});
export const SearchResponseSchema = z.strictObject({
    /** The query as received, echoed back so a stale response can be discarded. */
    query: z.string(),
    /** What we actually searched with, after `normalizeText`. May be `''`. */
    normalized: z.string(),
    backend: SearchBackend,
    groups: SearchGroupsSchema,
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/search/suggest
 * ──────────────────────────────────────────────────────────────────────────── */
export const SUGGEST_KINDS = ['track', 'artist', 'tag'];
export const SuggestKind = z.enum(SUGGEST_KINDS);
export const SuggestQuerySchema = z.object({
    q: z.string().min(1).max(64),
});
/** `sub` is the secondary line (performer for a track, track count for an artist). */
export const SuggestItemSchema = z.strictObject({
    kind: SuggestKind,
    id: RowId,
    label: NonEmptyText,
    sub: z.string().nullable(),
});
/** Max 8 items — §6.5. */
export const SUGGEST_MAX_ITEMS = 8;
export const SuggestResponseSchema = z.strictObject({
    items: z.array(SuggestItemSchema).max(SUGGEST_MAX_ITEMS),
});
//# sourceMappingURL=search.js.map